import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

const CYCLE_OPTIONS = [
  { value: "monthly", label: "Mensal", months: 1 },
  { value: "trimestral", label: "Trimestral", months: 3 },
  { value: "annual", label: "Anual", months: 12 },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

export default function UpgradeConfirmDialog({
  open,
  plan,
  initialCycle,
  onClose,
  onConfirm,
  loading,
}) {
  const [cycle, setCycle] = useState(initialCycle || "monthly");

  if (!plan) return null;

  const pricing = plan.pricing?.[cycle];
  const discount = pricing?.discount || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Confirmar Mudança de Plano
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Você está mudando para o plano <strong>{plan.name}</strong>.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecione o ciclo de cobrança:
        </Typography>

        <ToggleButtonGroup
          value={cycle}
          exclusive
          onChange={(_, v) => v && setCycle(v)}
          fullWidth
          sx={{ mb: 3 }}
        >
          {CYCLE_OPTIONS.map((opt) => {
            const optPricing = plan.pricing?.[opt.value];
            const optDiscount = optPricing?.discount || 0;
            return (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ textTransform: "none", flexDirection: "column", py: 1.5 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(optPricing?.price || 0)}
                </Typography>
                {optDiscount > 0 && (
                  <Chip
                    label={`-${optDiscount}%`}
                    color="success"
                    size="small"
                    sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
                  />
                )}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        <Stack
          spacing={1}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: "grey.50",
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Plano</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {plan.name}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Ciclo</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {CYCLE_OPTIONS.find((o) => o.value === cycle)?.label}
            </Typography>
          </Stack>
          {discount > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Desconto</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                -{discount}%
              </Typography>
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Total
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "primary.main" }}>
              {formatCurrency(pricing?.price || 0)}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(plan.plan_id, cycle)}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={20} /> : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
